/**
 * @fileoverview Utilidades para mapeo de tipos de respuesta rápida a iconos del sistema
 * @module AnswerTypeUtils
 * @category Utility
 * @since 1.0.0
 * @version 3.1.0 - Optimización y deprecación gradual, lógica movida a FastAnswerIconMC
 */

/**
 * @typedef {'all'|'audio'|'text_pdf'|'text_image'|'video'|string} AnswerType
 * @description Tipos disponibles para las respuestas rápidas
 * @property {'all'} all - Tipo para mostrar todas las respuestas
 * @property {'audio'} audio - Tipo para respuestas de audio
 * @property {'text_pdf'} text_pdf - Tipo para documentos PDF
 * @property {'text_image'} text_image - Tipo para imágenes
 * @property {'video'} video - Tipo para contenido de video
 * @property {string} default - Cualquier otro tipo será tratado como texto
 */

/**
 * Cache de mapeo de tipos a iconos para optimizar rendimiento.
 * Evita recrear el objeto de mapeo en cada llamada a la función.
 * 
 * @const {Object<string, string>} ICON_TYPE_MAP
 * @category Performance Cache
 * @private
 * 
 * @description
 * **Mapeo Optimizado:**
 * - Pre-calculado para evitar switch statements repetitivos
 * - Inmutable para garantizar estabilidad de referencias
 * - Lookup O(1) en lugar de O(n) del switch statement
 */
const ICON_TYPE_MAP = Object.freeze({
	all: 'menu-fill',
	audio: 'volume-up',
	text_pdf: 'document-pdf-16-regular',
	text_image: 'image-outline',
	video: 'video-outline'
});

/**
 * Icono por defecto para tipos no reconocidos.
 * Constante para evitar recreación en cada llamada.
 * 
 * @const {string} DEFAULT_ICON
 * @category Performance Constant
 * @private
 */
const DEFAULT_ICON = 'text-icon';

/**
 * Mapea el tipo de respuesta rápida al nombre del icono correspondiente de forma optimizada.
 * 
 * Función utilitaria compartida entre múltiples componentes con optimizaciones
 * de rendimiento para uso en listas grandes y renderizado frecuente.
 * 
 * @function iconType
 * @category Utility Function
 * 
 * @description
 * **Optimizaciones de Rendimiento (v3.1.0):**
 * - ✅ **Object Lookup**: O(1) en lugar de switch statement O(n)
 * - ✅ **Inmutable Cache**: Referencias estables para memoización
 * - ✅ **Zero Dependencies**: Función pura sin side effects
 * - ✅ **Memory Efficient**: Cache pre-calculado y congelado
 * 
 * **Mapeo de Iconos:**
 * - `all` → `menu-fill` - Icono de menú para todas las opciones
 * - `audio` → `volume-up` - Icono de volumen para audio
 * - `text_pdf` → `document-pdf-16-regular` - Icono para PDFs
 * - `text_image` → `image-outline` - Icono para imágenes
 * - `video` → `video-outline` - Icono para videos
 * - `default` → `text-icon` - Icono por defecto para tipos no reconocidos
 * 
 * **Componentes que la Utilizan:**
 * - FastAnswerIconMC (renderizado de iconos)
 * - FastAnswerTypeSelectMC (opciones de selector)
 * - Cualquier componente que necesite mapeo tipo → icono
 * 
 * @param {AnswerType} type - Tipo de respuesta rápida
 * 
 * @returns {string} Nombre del icono correspondiente
 * 
 * @example
 * ```javascript
 * // Uso directo (optimizado)
 * const audioIcon = iconType('audio'); // Retorna: 'volume-up'
 * const unknownIcon = iconType('unknown'); // Retorna: 'text-icon'
 * 
 * // En componentes con memoización
 * const iconName = useMemo(() => iconType(type), [type]);
 * 
 * // En selectores de opciones
 * const options = types.map(type => ({
 *   value: type,
 *   icon: iconType(type),
 *   label: getLabel(type)
 * }));
 * ```
 * 
 * @see {@link FastAnswerIconMC} Componente que usa esta función con memoización
 * @see {@link FastAnswerTypeSelectMC} Selector que usa esta función
 * 
 * @since 1.0.0
 * @version 3.1.0 - Optimización completa con object lookup y cache inmutable
 */
export const iconType = (type) => {
	return ICON_TYPE_MAP[type] || DEFAULT_ICON;
};
